import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import {
  hasEdgeTeamScope,
  SCOPE_EDGE_COMPANIES,
  SCOPE_EDGE_LICENSES,
  SCOPE_EDGE_SERVERS,
  SCOPE_EDGE_USERS,
  SCOPE_SURVEYS_VIEW,
} from '../config/apps'
import type { UserRole } from '../types'
import { StackMark } from './Lamp'
import ThemeToggle from './ThemeToggle'
import { IconBoard, IconBox, IconClipboard, IconExit, IconKey, IconPeople, IconRailClose, IconRailOpen, IconSheet, IconSwitch } from './icons'

type Can = (scope: string) => boolean
type NavItem = { path: string; label: string; Icon: typeof IconBoard; visible: (role: UserRole, can: Can) => boolean }
type NavGroup = { label: string; items: NavItem[] }

const ROLE_LABEL: Record<string, string> = { Admin: '관리자', Manager: '매니저', User: '사용자', Viewer: '뷰어' }

// Viewer 는 이 레이아웃에 오지 않는다(ViewerDashboard 격리) — role 은 Admin/Manager/User.
const navGroups: NavGroup[] = [
  {
    label: '운영',
    items: [
      { path: '/', label: '대시보드', Icon: IconBoard, visible: () => true },
      { path: '/surveys', label: '설비조사', Icon: IconClipboard, visible: (_, can) => can(SCOPE_SURVEYS_VIEW) },
      // 설비조사 → 디바이스 명세 → 서버 세팅 순서. 태그 주소 등 고객사 내부 설정이라 Edge팀만 (API ServersAdmin)
      { path: '/device-specs', label: '디바이스 명세', Icon: IconSheet, visible: (_, can) => can(SCOPE_EDGE_SERVERS) },
    ],
  },
  {
    label: '리소스',
    items: [
      { path: '/licenses', label: '라이센스', Icon: IconKey, visible: (role, can) => role === 'Manager' || can(SCOPE_EDGE_LICENSES) },
      { path: '/harbor', label: 'Harbor', Icon: IconBox, visible: (_, can) => can(SCOPE_EDGE_SERVERS) },
    ],
  },
  // 회사·사용자 관리는 EDGE 콘솔(/admin, 플랫폼 층)로 이동 — 하단 "조직 관리" 링크로 진입
]

// 서버 상세(/servers/:id)는 대시보드 아래 화면이라 대시보드를 켜 둔다
const isActive = (itemPath: string, pathname: string) =>
  itemPath === '/' ? pathname === '/' || pathname.startsWith('/servers') : pathname.startsWith(itemPath)

const footLink = 'flex items-center gap-2.5 py-1.5 rounded-md text-[13px] text-gray-500 hover:text-gray-900 hover:bg-gray-200/60 transition-colors'

// 사이드바 접힘은 브라우저별 편의 설정이라 localStorage 에만 둔다
const RAIL_KEY = 'sidebarCollapsed'
const readCollapsed = () => { try { return localStorage.getItem(RAIL_KEY) === '1' } catch { return false } }

export default function Layout({ children }: { children: React.ReactNode }) {
  const { role, scopes, apps, logout, can } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(readCollapsed)

  const toggleRail = () => {
    const next = !collapsed
    setCollapsed(next)
    try { localStorage.setItem(RAIL_KEY, next ? '1' : '0') } catch { /* 무시 */ }
  }

  const handleLogout = () => { logout(); navigate('/login') }

  // 접히면 글자를 숨기고 아이콘만 가운데 — 글자는 title 툴팁·aria-label 로
  const pad = collapsed ? 'justify-center px-0' : 'px-3'
  const tip = (label: string) => (collapsed ? { title: label, 'aria-label': label } : {})

  const visibleGroups = navGroups
    .map(group => ({
      ...group,
      items: group.items.filter(item => role && item.visible(role, can)),
    }))
    .filter(group => group.items.length > 0)

  return (
    <div className="flex h-screen bg-canvas">
      <aside className={`${collapsed ? 'w-14' : 'w-60'} shrink-0 bg-rail border-r border-gray-200 flex flex-col overflow-hidden transition-[width] duration-200`}>
        <div className={`flex items-center h-16 shrink-0 ${collapsed ? 'justify-center' : 'pl-5 pr-3'}`}>
          {!collapsed && (
            <Link to="/" className="flex items-center gap-3 min-w-0 flex-1">
              <StackMark />
              <span className="text-[15px] font-semibold tracking-tight text-gray-900 truncate">EdgeManager</span>
            </Link>
          )}
          <button type="button" onClick={toggleRail} aria-expanded={!collapsed}
            title={collapsed ? '사이드바 펴기' : '사이드바 접기'} aria-label={collapsed ? '사이드바 펴기' : '사이드바 접기'}
            className="grid place-items-center w-8 h-8 rounded-md text-gray-400 hover:text-gray-900 hover:bg-gray-200/60 transition-colors">
            {collapsed ? <IconRailOpen /> : <IconRailClose />}
          </button>
        </div>

        <nav className={`flex-1 pt-2 pb-4 overflow-y-auto overflow-x-hidden ${collapsed ? 'px-2' : 'px-3'}`} aria-label="주 메뉴">
          {visibleGroups.map((group, idx) => (
            <div key={group.label} className={idx === 0 ? '' : collapsed ? 'mt-3 pt-3 border-t border-gray-200' : 'mt-6'}>
              {!collapsed && <div className="px-3 pb-1.5 text-xs text-gray-500">{group.label}</div>}
              <div className="space-y-0.5">
                {group.items.map(item => {
                  const active = isActive(item.path, location.pathname)
                  return (
                    <Link key={item.path} to={item.path} aria-current={active ? 'page' : undefined} {...tip(item.label)}
                      className={`relative flex items-center gap-2.5 py-2 rounded-md text-sm transition-colors ${pad} ${
                        active
                          ? 'bg-surface text-gray-900 font-medium shadow-sm ring-1 ring-gray-200'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/60'
                      }`}>
                      {active && <span aria-hidden className={`absolute ${collapsed ? '-left-2' : '-left-3'} top-2 bottom-2 w-[3px] rounded-r bg-accent`} />}
                      <item.Icon className={`w-4 h-4 shrink-0 ${active ? 'text-accent' : 'text-gray-400'}`} />
                      {!collapsed && item.label}
                    </Link>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className={`pt-3 pb-4 border-t border-gray-200 space-y-0.5 ${collapsed ? 'px-2' : 'px-3'}`}>
          {(role === 'Manager' || can(SCOPE_EDGE_USERS) || can(SCOPE_EDGE_COMPANIES)) && (
            <Link to="/admin" {...tip('조직 관리')} className={`${footLink} ${pad}`}>
              <IconPeople className="w-4 h-4 shrink-0" />{!collapsed && '조직 관리'}
            </Link>
          )}
          {apps.length > 1 && (
            <Link to="/apps" {...tip('다른 서비스로 이동')} className={`${footLink} ${pad}`}>
              <IconSwitch className="w-4 h-4 shrink-0" />{!collapsed && '다른 서비스로 이동'}
            </Link>
          )}
          <button onClick={handleLogout} {...tip('로그아웃')} className={`w-full text-left ${footLink} ${pad}`}>
            <IconExit className="w-4 h-4 shrink-0" />{!collapsed && '로그아웃'}
          </button>

          {collapsed ? (
            <div className="flex justify-center pt-3"><ThemeToggle vertical /></div>
          ) : (
            <div className="flex items-center justify-between gap-2 px-3 pt-3">
              <span className="text-xs text-gray-500 truncate">
                {ROLE_LABEL[role ?? ''] ?? role}{hasEdgeTeamScope(scopes) ? ', Edge팀' : ''}
              </span>
              <ThemeToggle />
            </div>
          )}
        </div>
      </aside>

      {/* 폭 상한 없이 남는 화면을 다 쓴다 (예전 1440px 상한은 넓은 모니터에서 오른쪽을 비워 뒀다) */}
      <main className="flex-1 min-w-0 overflow-auto p-8">
        {/* h-full: 서버 상세는 헤더를 고정하고 안쪽만 스크롤하려고 main 높이를 채운다 */}
        <div className="h-full">
          {children}
        </div>
      </main>
    </div>
  )
}
